<script setup lang="ts">
/**
 * BON07 — Statement Containing Particulars of Shares Allotted Otherwise Than for Cash (Bonus Issue).
 * Word 템플릿 문장/조건(has_constitution, clause_2/3, resolution_basis_dividend|reserve)에 맞춤.
 */
import { computed, inject } from 'vue';
import { BONUS_DOC_CTX } from './bonusContext';
import BonusDocVarField from './BonusDocVarField.vue';
import BonusAllotteeTable from './parts/BonusAllotteeTable.vue';
import BonusSigners from './parts/BonusSigners.vue';

const ctx = inject(BONUS_DOC_CTX)!;
const hasConstitution = computed(() => ctx.flag('has_constitution'));
const clause2 = computed(() => ctx.flag('clause_2_applicable'));
const clause3 = computed(() => ctx.flag('clause_3_applicable'));
const stBasis = computed(() => String(ctx.choiceOf('statement_resolution_basis') || 'reserve'));

/** 표(allottee_name_*)에서 자동 집계 — 추가/삭제 시 본문 이름 목록 동기화 */
const allotteeNamesSummary = computed(() => {
  const names: string[] = [];
  for (let n = 1; n <= ctx.allotteeCount.value; n++) {
    const name = String(ctx.valueOf(`allottee_name_${n}`) || '').trim();
    if (name) names.push(name);
  }
  return names.join(', ');
});
</script>

<template>
  <div class="allot-doc">
    <p class="doc-p">
      Company Registration No. <BonusDocVarField field-key="company_registration_no" />
    </p>
    <p class="doc-sec-title">
      STATEMENT CONTAINING PARTICULARS OF SHARES ALLOTTED OTHERWISE THAN FOR CASH
    </p>
    <p class="doc-p" style="text-align:center">
      <BonusDocVarField field-key="company_name" />
    </p>

    <p class="doc-p">
      1. The allotment of <BonusDocVarField field-key="total_new_shares" /> shares of
      RM<BonusDocVarField field-key="share_price" /> each to
      <span
        class="doc-auto-names"
        :class="{ empty: !allotteeNamesSummary }"
        :title="'Synced from allottee table'"
      >{{ allotteeNamesSummary || 'Allottee Names (from table)' }}</span>
      on the <BonusDocVarField field-key="allotment_date" /> was made pursuant to a provision in the
      <span
        class="doc-toggle"
        title="Constitution present?"
        @click="ctx.toggleFlag('has_constitution')"
      >{{ hasConstitution ? 'Constitution' : 'memorandum or articles' }}</span>.
    </p>

    <p class="doc-p">
      <span class="doc-toggle" @click="ctx.toggleFlag('clause_2_applicable')">{{
        clause2 ? '*2. (APPLIES)' : '*2. (does NOT apply)'
      }}</span>
      Particulars sufficient to show the entitlement of the allottees to the allotment of shares
      where the allotment was made pursuant to a contract not reduced to writing are as follows:
      <template v-if="clause2"> — see contract particulars on file.</template>
    </p>

    <p class="doc-p">
      <span class="doc-toggle" @click="ctx.toggleFlag('clause_3_applicable')">{{
        clause3 ? '*3. (APPLIES)' : '*3. (does NOT apply)'
      }}</span>
      The particulars of the resolution or other authority by virtue of which the allotment of
      shares was made are as follows:
    </p>
    <template v-if="clause3">
      <p class="doc-p" style="margin-left:12px">
        <span
          class="doc-toggle"
          @click="ctx.setChoice('statement_resolution_basis', 'dividend')"
        >{{ stBasis === 'dividend' ? '☑' : '☐' }}</span>
        an allotment was made in satisfaction of a dividend declared in favour of, but not payable
        in cash to, the shareholders.
      </p>
      <p class="doc-p" style="margin-left:12px">
        <span
          class="doc-toggle"
          @click="ctx.setChoice('statement_resolution_basis', 'reserve')"
        >{{ stBasis === 'reserve' ? '☑' : '☐' }}</span>
        an account or reserve was applied directly in paying up shares.
      </p>
    </template>

    <p class="doc-p">
      4. The particulars of the consideration in respect of which the allotment of shares was made
      are as follows:
    </p>

    <!-- Statement 템플릿: Name / Address / Shares Otherwise / Amount Capitalized -->
    <BonusAllotteeTable
      :show-amount="true"
      statement-keys
      shares-header="No. of Shares Allotted Otherwise than in Cash"
    />

    <p class="doc-p" style="margin-top:14px">
      THAT the <BonusDocVarField field-key="total_new_shares" /> shares allotted to
      <span
        class="doc-auto-names"
        :class="{ empty: !allotteeNamesSummary }"
        :title="'Synced from allottee table'"
      >{{ allotteeNamesSummary || 'Allottee Names (from table)' }}</span>
      is/are by way of capitalization of the
      <BonusDocVarField field-key="reserve_type" />
      of the Company.
    </p>

    <p class="doc-p">Dated this <BonusDocVarField field-key="statement_date" /></p>
    <BonusSigners var-key="statement" />

    <p class="doc-p" style="margin-top:18px;font-size:0.92em">
      <b>Attention:</b>
      It is an offence under section 591 of the Companies Act 2016 to make or authorize the making
      of a statement that a person knows is false or misleading and that person may be liable,
      upon conviction, to imprisonment for a term not exceeding ten years or to a fine not
      exceeding RM3 million or to both.
    </p>
  </div>
</template>
